/**
 * @file AiSettingsSimple.tsx
 * @brief Super Admin → Settings, the simple AI page (DES 15-10-2026: "settings
 *        wala page bari confusing hai ... simple ... easy to change and
 *        understand"; mockup approved the same day).
 *
 * @details
 * Same `system_settings` rows as AiConfigurationCard, nothing new on the
 * backend. A bar lists every change (old → new) and its cost difference
 * before Save; nothing is written until Save. Every other setting (provider
 * pickers, voice and transcription models, ASR timeouts, routing rollback)
 * stays in the full card below the page ("All settings (technical)").
 *
 * W13 (DES 23-10-2026, design locked: scratch/dispatcher/w13-ai-settings-mockup.html):
 * one module at a time as a pipeline. Module tabs (Interpreter, Meetings,
 * Notes, Ask) with cost per hour and unsaved count; a summary card (active
 * preset, one sentence, total, where the money goes, language and preset
 * pills); a vertical timeline of steps (main vendor, model, cost, "If it
 * fails" backups) beside a step inspector. Modules, steps, presets and costs
 * live in ./aiPipeline.ts (pure, tested); this file only draws them.
 *
 * Vendor marks are letter badges. An official logo dropped in
 * src/assets/vendors/<vendor>.svg replaces its letter, drawn unmodified.
 *
 * @author DES
 * @date 15-10-2026
 * @updated 23-10-2026 — W13 pipeline layout (aiPipeline.ts)
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowDown,
  ArrowUp,
  AudioLines,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  Compass,
  FileText,
  Info,
  Languages,
  LoaderCircle,
  MessageCircleQuestion,
  Mic,
  NotebookPen,
  PenLine,
  RotateCcw,
  ScanSearch,
  Search,
  ShieldCheck,
  UserRound,
  Users,
  Volume2,
} from "lucide-react";
import { Skeleton } from "@/components/ui/Skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { useSystemSettings, useUpdateSettings } from "@/features/billing/api";
import { useAiCatalog, type AiCatalog } from "../api";
import { FALLBACK_AI_CATALOG, MT_MODEL_OPTIONS } from "../schemas";
import { DEFAULTS } from "./AiConfigurationCard";
import { urduVoiceSteps } from "./urduVoicePickers.ts";
import {
  BADGE_LETTER,
  LANGS,
  MODULES,
  USER_PENCE_PER_HOUR,
  chainFor,
  chainPatch,
  changedKeys,
  fmtP,
  isRetired,
  RETIRED_NOTE,
  moduleCost,
  moduleFields,
  moduleSteps,
  moveBackup,
  pence,
  pickPatch,
  presetOf,
  presetsFor,
  wasText,
  type Ctx,
  type IconKey,
  type LangKey,
  type ModuleDef,
  type ModuleKey,
  type StepView,
  type Switch,
  type TextCard,
} from "./aiPipeline.ts";

const ICONS: Record<IconKey, typeof Mic> = {
  listen: AudioLines,
  translate: Languages,
  speak: Volume2,
  names: UserRound,
  write: PenLine,
  check: ShieldCheck,
  second: ScanSearch,
  plan: Compass,
  search: Search,
  mic: Mic,
  voice: Volume2,
  doc: FileText,
  question: MessageCircleQuestion,
};
const MODULE_ICONS: Record<ModuleKey, typeof Mic> = {
  interpreter: Languages,
  meetings: Users,
  notes: NotebookPen,
  ask: MessageCircleQuestion,
};

/** Official vendor logos, if DES has dropped them in (never redrawn here). */
const LOGOS = import.meta.glob<string>("../../../assets/vendors/*.svg", { eager: true, query: "?url", import: "default" });

/** The section and inner-card looks of the 15-10 page. */
const SECTION = "rounded-3xl bg-black/[0.03] dark:bg-white/[0.04]";
const CARD = "rounded-2xl border border-transparent bg-black/[0.03] hover:bg-black/[0.06] dark:bg-white/[0.04] dark:hover:bg-white/[0.08]";
const CARD_ON = "border-brand bg-brand/[0.06] hover:bg-brand/[0.06] dark:bg-brand/[0.06] dark:hover:bg-brand/[0.06]";
const CARD_CHANGED = "border-amber-500 bg-amber-500/10 hover:bg-amber-500/10 dark:bg-amber-500/10 dark:hover:bg-amber-500/10";
/** A round node over the rail: opaque page colour with the section's tint on top. */
const NODE_BG = "bg-background shadow-[inset_0_0_0_40px_rgb(0_0_0/0.03)] dark:shadow-[inset_0_0_0_40px_rgb(255_255_255/0.04)]";
/** Where-the-money-goes shades, brand red only. */
const SHADES = ["bg-brand", "bg-brand/75", "bg-brand/55", "bg-brand/40", "bg-brand/25"];
const KICKER = "text-[11px] font-medium uppercase tracking-[0.09em] text-app-text-secondary";

function VendorBadge({ id }: { id: string }) {
  const logo = LOGOS[`../../../assets/vendors/${id}.svg`];
  if (logo) return <img src={logo} alt="" aria-hidden className="h-6 w-6 shrink-0 object-contain" />;
  return (
    <span
      aria-hidden
      className="inline-grid h-6 w-6 shrink-0 place-items-center rounded-[7px] border border-border bg-background text-xs font-semibold text-app-text"
    >
      {BADGE_LETTER[id] ?? id.charAt(0).toUpperCase()}
    </span>
  );
}

function Seg<T extends string>({
  label,
  items,
  value,
  onPick,
  brand,
  ariaLabel,
}: {
  label: string;
  ariaLabel?: string;
  items: { value: T; label: string; title?: string }[];
  value: T | null;
  onPick: (v: T) => void;
  brand?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {label ? <span className={KICKER}>{label}</span> : null}
      <div role="group" aria-label={label || ariaLabel} className="inline-flex flex-wrap gap-0.5 rounded-[11px] bg-black/[0.05] p-[3px] dark:bg-white/[0.06]">
        {items.map((it) => (
          <button
            key={it.value}
            type="button"
            title={it.title}
            aria-pressed={it.value === value}
            onClick={() => onPick(it.value)}
            className={cn(
              "rounded-full px-4 py-1.5 text-[13px] text-app-text-secondary",
              it.value === value && "bg-background text-app-text shadow-sm",
              it.value === value && brand && "text-brand",
            )}
          >
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Backups({ step }: { step: StepView }) {
  return (
    <span className="mt-2 flex flex-wrap items-center gap-1.5 text-[11.5px] text-app-text-secondary">
      <span className="mr-0.5 text-[10.5px] uppercase tracking-[0.08em]">If it fails</span>
      {step.backups?.length
        ? step.backups.map((b, j) => (
            <span key={b} className="contents">
              {j ? <span aria-hidden>›</span> : null}
              <span className="whitespace-nowrap rounded-full border border-border px-2 text-app-text">{b}</span>
            </span>
          ))
        : <span>{step.backText}</span>}
    </span>
  );
}

export function AiSettingsSimple({ advanced }: { advanced: ReactNode }) {
  const { data, isLoading, isError } = useSystemSettings();
  const catalogQuery = useAiCatalog();
  const catalog: AiCatalog = catalogQuery.data ?? FALLBACK_AI_CATALOG;
  const update = useUpdateSettings();

  const saved = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = { ...DEFAULTS };
    for (const [key, row] of Object.entries(data?.settings ?? {})) if (row?.value) out[key] = row.value;
    return out;
  }, [data]);
  const [values, setValues] = useState<Record<string, string>>(saved);
  useEffect(() => setValues(saved), [saved]);

  const [cur, setCur] = useState<ModuleKey>("interpreter");
  const [langs, setLangs] = useState<Record<ModuleKey, LangKey>>({ interpreter: "english", meetings: "english", notes: "english", ask: "english" });
  const [sel, setSel] = useState<Record<ModuleKey, string>>(() =>
    Object.fromEntries(MODULES.map((m) => [m.key, m.steps[0]])) as Record<ModuleKey, string>,
  );
  const [showAll, setShowAll] = useState(false);

  const provider = saved.LLM_PROVIDER || "openai";
  // A switch whose row the server lacks would save nothing, so it is shown off and locked.
  const serverHas = (key: string) => Boolean(data?.settings?.[key]);
  const ctxFor = (m: ModuleDef): Ctx => ({ provider, catalog, lang: langs[m.key], mtModels: [...MT_MODEL_OPTIONS] });
  const patch = (p: Record<string, string | undefined>) =>
    setValues((all) => ({ ...all, ...(Object.fromEntries(Object.entries(p).filter(([, x]) => x !== undefined)) as Record<string, string>) }));

  /** Every changed field once, under the first module that owns it. */
  const changes = useMemo(() => {
    const seen = new Set<string>();
    const out: { module: ModuleDef; key: string; label: string; from: string; to: string }[] = [];
    for (const m of MODULES) {
      const ctx: Ctx = { provider, catalog, lang: "english", mtModels: [...MT_MODEL_OPTIONS] };
      for (const f of changedKeys(moduleFields(m, values, ctx), values, saved)) {
        if (seen.has(f.key)) continue;
        seen.add(f.key);
        out.push({ module: m, key: f.key, label: f.label, from: f.fmt(saved[f.key]), to: f.fmt(values[f.key]) });
      }
    }
    return out;
  }, [values, saved, provider, catalog]);

  const delta = MODULES.reduce((sum, m) => {
    const ctx = ctxFor(m);
    return sum + (moduleCost(moduleSteps(m, values, ctx)) ?? 0) - (moduleCost(moduleSteps(m, saved, ctx)) ?? 0);
  }, 0);

  // The server drops a value it refuses without an error, so Save waits until it is fixed.
  const textProblem = MODULES.map((m) => (m.textCard && changes.some((c) => c.key === m.textCard!.key) ? m.textCard.problem(values[m.textCard.key]) : null)).find(Boolean) ?? null;

  const save = async () => {
    const body = Object.fromEntries(changes.map((c) => [c.key, values[c.key]]));
    try {
      await update.mutateAsync({ settings: body });
      toast({ variant: "success", title: "AI settings saved", description: "They apply from the next meeting or session." });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 px-4 py-6 md:px-5">
        <Skeleton className="h-24 w-full rounded-3xl" />
        <Skeleton className="h-48 w-full rounded-3xl" />
      </div>
    );
  }
  if (isError) {
    return <p className="px-4 py-6 text-sm text-muted-foreground md:px-5">Could not load the AI settings.</p>;
  }

  const mod = MODULES.find((m) => m.key === cur)!;
  const ctx = ctxFor(mod);
  const lang = langs[cur];
  const langLabel = LANGS.find((l) => l.key === lang)!.label;
  const steps = moduleSteps(mod, values, ctx);
  const savedSteps = moduleSteps(mod, saved, ctx);
  const total = moduleCost(steps);
  const presets = presetsFor(cur, provider);
  const activeSaved = presetOf(presets, saved);
  const activeDraft = presetOf(presets, values);
  const notice = mod.notice?.(values);
  const step = steps.find((s) => s.id === sel[cur]) ?? steps[0];
  // A Listen card turns amber only for its own language's order (or the chain switch).
  const otherChains = new Set(LANGS.filter((l) => l.key !== lang).map((l) => l.chainKey));
  const changedIn = (s: StepView) =>
    s.fields.some((f) => !(s.perLang && otherChains.has(f.key)) && (values[f.key] ?? "") !== (saved[f.key] ?? ""));
  const perHour = cur === "meetings" || cur === "notes";
  const per = cur === "ask" ? "/100q" : "/h";

  return (
    <div className="mx-auto max-w-[1120px] px-4 pb-40 pt-6 text-app-text md:px-5">
      <p className="mb-4 max-w-[62ch] px-1 text-[15px] text-app-text-secondary">
        Follow each module from sound in to result out. Pick a step to change it. Nothing changes until you press Save.
      </p>

      {/* 1. Module tabs */}
      <div role="tablist" aria-label="Modules" className="mb-4 grid grid-cols-2 gap-2.5 xl:grid-cols-4">
        {MODULES.map((m) => {
          const Icon = MODULE_ICONS[m.key];
          const on = m.key === cur;
          const c = moduleCost(moduleSteps(m, values, ctxFor(m)));
          const n = changedKeys(moduleFields(m, values, ctxFor(m)), values, saved).length;
          return (
            <button
              key={m.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setCur(m.key)}
              className={cn("flex min-w-0 items-center gap-3 px-4 py-3.5 text-left text-app-text max-sm:p-2.5", CARD, on && CARD_ON)}
            >
              <Icon className={cn("h-[22px] w-[22px] shrink-0", on ? "text-brand" : "text-app-text-secondary")} strokeWidth={1.75} />
              <span className="min-w-0">
                <b className={cn("block truncate text-[14.5px] font-semibold", on && "text-brand")}>{m.title}</b>
                <small className="block truncate text-xs tabular-nums text-app-text-secondary max-sm:hidden">
                  {c === null ? "No estimate yet" : `${fmtP(c)} ${m.tabUnit}`}
                </small>
              </span>
              {n ? (
                <span className="ml-auto whitespace-nowrap rounded-full bg-amber-500/10 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700 dark:text-amber-300 max-sm:h-2 max-sm:w-2 max-sm:bg-amber-500 max-sm:p-0 max-sm:text-[0px]">
                  {n} unsaved
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* 2. Summary card */}
      <section className={cn(SECTION, "mb-4 p-6 max-sm:p-4")} aria-live="polite">
        <div className="flex flex-wrap items-start justify-between gap-x-7 gap-y-4">
          <div className="min-w-0 flex-[1_1_320px]">
            <div className="flex flex-wrap items-center gap-2.5">
              <h3 className="text-[22px] font-bold">{mod.title}</h3>
              {presets.length ? (
                activeSaved ? (
                  <span className="rounded-full border border-brand/40 bg-brand/[0.06] px-2.5 py-0.5 text-[11px] font-semibold text-brand">
                    Active: {activeSaved.label}
                  </span>
                ) : (
                  <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                    Custom setup
                  </span>
                )
              ) : null}
            </div>
            <p className="mt-2 max-w-[70ch] text-app-text-secondary">
              {mod.noLang ? "" : `${langLabel}: `}
              {steps.map((s, i) => (
                <span key={s.id}>
                  {i ? ", then " : ""}
                  <b className="font-medium text-app-text">{s.vendor}</b> {s.verb}
                </span>
              ))}
              .
            </p>
          </div>
          <div className="min-w-[140px] text-right max-sm:text-left">
            <div className="text-[34px] font-bold leading-none tabular-nums tracking-tight">{total === null ? "—" : fmtP(total)}</div>
            <div className="mt-1 text-xs text-app-text-secondary">
              {total === null ? "no estimate yet" : mod.unit}
              {mod.noLang ? "" : ` · ${langLabel}`}
              {total !== null && mod.estimate ? ` · ${mod.estimate}` : ""}
            </div>
            {perHour && total !== null ? (
              <div className="mt-0.5 text-xs text-app-text-secondary">
                User pays £3.00 ·{" "}
                <span className="text-emerald-500">{(((USER_PENCE_PER_HOUR - pence(total)) / USER_PENCE_PER_HOUR) * 100).toFixed(0)}% margin</span>
              </div>
            ) : null}
          </div>
        </div>

        {notice ? (
          <p className="mt-4 rounded-2xl bg-amber-500/10 px-4 py-2.5 text-[13px] text-amber-700 dark:text-amber-300">{notice}</p>
        ) : null}

        <div className="mt-[18px]">
          <div className={cn(KICKER, "mb-[7px] flex justify-between")}>
            <span>Where the money goes</span>
            <span className="normal-case tracking-normal tabular-nums">{total === null ? "" : fmtP(total)}</span>
          </div>
          {total === null ? (
            <p className="text-[13px] text-app-text-secondary">No cost estimate for this module in our cost runs yet.</p>
          ) : (
            <>
              <div role="img" aria-label="Cost split by step" className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-black/[0.05] dark:bg-white/[0.06]">
                {steps.map((s, i) => (
                  <span key={s.id} className={cn("h-full transition-[flex-grow] duration-300", SHADES[i % SHADES.length])} style={{ flexGrow: Math.max(s.cost ?? 0, 0.0001) }} />
                ))}
              </div>
              <div className="mt-[9px] flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-app-text-secondary">
                {steps.map((s, i) => (
                  <span key={s.id}>
                    <i className={cn("mr-1.5 inline-block h-2 w-2 rounded-[2px] align-[1px]", SHADES[i % SHADES.length])} />
                    {s.name}
                    <b className="ml-1 font-medium tabular-nums text-app-text">{s.cost === undefined ? "—" : fmtP(s.cost)}</b>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-[18px] flex flex-wrap items-center gap-x-[22px] gap-y-3 border-t border-border/60 pt-4">
          {mod.noLang ? null : (
            <Seg label="Language" items={LANGS.map((l) => ({ value: l.key, label: l.label }))} value={lang} onPick={(l) => setLangs((x) => ({ ...x, [cur]: l }))} />
          )}
          {presets.length ? (
            <Seg
              label="Setup"
              brand
              items={presets.map((p) => ({ value: p.key, label: p.label, title: p.note }))}
              value={activeDraft?.key ?? null}
              onPick={(k) => patch(presets.find((p) => p.key === k)!.values)}
            />
          ) : (
            <p className="text-[13px] text-app-text-secondary">
              Ready-made choices are written for OpenAI. Your provider is {catalog.LLM[provider]?.label ?? provider}: choose each step.
            </p>
          )}
        </div>
        {presets.length ? (
          <p className="mt-2.5 text-[13px] text-app-text-secondary">{activeDraft ? activeDraft.note : "Custom setup: no ready-made choice matches these steps."}</p>
        ) : null}
      </section>

      {/* 3 + 4. Timeline and step inspector */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className={cn(SECTION, "p-6 max-sm:p-3.5")}>
          <div className="relative flex flex-col gap-3.5">
            <span aria-hidden className="absolute bottom-5 left-[19px] top-5 w-0.5 rounded-full bg-border" />
            <span
              aria-hidden
              className="absolute left-4 h-2 w-2 rail-travel rounded-full bg-app-text opacity-0 shadow-[0_0_0_4px_hsl(var(--app-text)/0.15)]"
            />
            <Endpoint icon={mod.in[0]} label={mod.in[1]} />
            {steps.map((s, i) => {
              const Icon = ICONS[s.icon];
              const on = s.id === step.id;
              const changed = changedIn(s);
              const was = changed ? wasText(s, savedSteps[i]) : null;
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setSel((x) => ({ ...x, [cur]: s.id }))}
                  className="group relative grid w-full grid-cols-[40px_minmax(0,1fr)] gap-3.5 text-left text-app-text"
                >
                  <span
                    className={cn(
                      "relative z-[1] mt-3 grid h-10 w-10 place-items-center rounded-full border-2 border-app-text-secondary/60 text-app-text-secondary transition-colors",
                      NODE_BG,
                      changed && "border-amber-500",
                      on && "border-brand bg-brand text-white shadow-none dark:shadow-none",
                    )}
                  >
                    <Icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
                  </span>
                  <span className={cn("block min-w-0 px-4 py-3 transition-colors", CARD, !on && !changed && "group-hover:bg-black/[0.06] dark:group-hover:bg-white/[0.08]", on && !changed && CARD_ON, changed && CARD_CHANGED)}>
                    <span className="flex items-baseline justify-between gap-2.5">
                      <span className={cn("text-[11px] font-semibold uppercase tracking-[0.09em] text-app-text-secondary", on && "text-brand")}>
                        <span className="mr-2 font-mono tracking-normal opacity-70">{i + 1}</span>
                        {s.name}
                      </span>
                      <span className="text-[15px] font-semibold tabular-nums">
                        {s.cost === undefined ? <span className="text-app-text-secondary">—</span> : fmtP(s.cost)}
                        {s.cost ? <small className="ml-0.5 text-[11px] font-normal text-app-text-secondary">{per}</small> : null}
                      </span>
                    </span>
                    <span className="mt-[3px] flex flex-wrap items-center gap-2">
                      <VendorBadge id={s.badge} />
                      <b className="text-[17px] font-bold">{s.vendor}</b>
                      {s.tag ? <span className="rounded-md bg-black/[0.04] px-2 py-0.5 text-[11.5px] text-app-text-secondary dark:bg-white/[0.06]">{s.tag}</span> : null}
                    </span>
                    {was ? (
                      <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
                        Was <s>{was}</s>
                      </span>
                    ) : null}
                    <Backups step={s} />
                  </span>
                </button>
              );
            })}
            <Endpoint icon={mod.out[0]} label={mod.out[1]} />
          </div>
        </div>

        <Inspector
          step={step}
          mod={mod}
          ctx={ctx}
          values={values}
          saved={saved}
          serverHas={serverHas}
          langLabel={langLabel}
          onPatch={patch}
        />
      </div>

      {mod.card ? (
        <section aria-label={mod.card.title} className={cn(SECTION, "mt-4 p-6 max-sm:p-4")}>
          <h3 className="text-lg font-bold">{mod.card.title}</h3>
          <p className="mt-0.5 text-[12.5px] text-app-text-secondary">{mod.card.intro}</p>
          <div className="mt-4 flex flex-col gap-2">
            {mod.card.items.map((c) => (
              <SwitchRow key={c.key} sw={c} missing={mod.card!.missing} ready={serverHas(c.key)} values={values} saved={saved} onPatch={patch} />
            ))}
          </div>
        </section>
      ) : null}

      {mod.textCard ? (
        <TextSettingCard
          card={mod.textCard}
          ready={serverHas(mod.textCard.key)}
          toggleReady={mod.textCard.toggle ? serverHas(mod.textCard.toggle.key) : false}
          values={values}
          saved={saved}
          onPatch={patch}
        />
      ) : null}

      <p className="mt-3 flex items-center gap-1.5 px-1 text-xs text-app-text-secondary">
        <Info className="h-3.5 w-3.5 shrink-0" />
        Costs are in pence: Meetings and Notes from our October cost runs, Interpreter and Ask AI from vendor list prices. Changes apply from the next meeting, no redeploy.
      </p>

      <button
        type="button"
        onClick={() => setShowAll((x) => !x)}
        aria-expanded={showAll}
        className="mt-8 inline-flex items-center gap-1 text-sm font-medium text-app-text-secondary hover:text-app-text"
      >
        <ChevronDown className={cn("h-4 w-4 transition-transform", showAll && "rotate-180")} />
        All settings (technical)
      </button>
      {/* The full card draws only right and bottom lines (it sat in a
          GridBlock): this frame adds the left and top ones (DES 15-10-2026:
          "aik side ka border hai hi nahin"). */}
      {showAll ? <div className="mt-3 border-l border-t border-border/60">{advanced}</div> : null}

      {changes.length > 0 ? (
        <div
          role="region"
          aria-label="Unsaved changes"
          className="fixed inset-x-0 bottom-6 z-20 mx-auto w-[min(880px,calc(100%-32px))] rounded-3xl bg-[#1C1C1C] p-5 text-white shadow-2xl dark:bg-app-quaternary"
        >
          <div className="flex items-start gap-4 max-sm:flex-col max-sm:items-stretch">
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold">
                {changes.length} {changes.length === 1 ? "change" : "changes"} not saved
                {Math.abs(delta) >= 0.05 ? (
                  <span className={cn("ml-2 tabular-nums", delta > 0 ? "text-amber-400" : "text-emerald-400")}>
                    {delta > 0 ? "+" : "−"}
                    {fmtP(Math.abs(delta))} per hour
                  </span>
                ) : null}
              </div>
              <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto text-[13px] text-white/70">
                {changes.map((c) => (
                  <li key={c.key}>
                    {c.module.title} · {c.label}: {c.from} → {c.to}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setValues(saved)}
                className="inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-sm font-medium text-white/80 hover:bg-white/10"
              >
                <RotateCcw className="h-4 w-4" /> Discard
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={update.isPending || Boolean(textProblem)}
                title={textProblem ?? undefined}
                className="inline-flex h-10 items-center gap-1.5 rounded-full bg-brand px-5 text-sm font-semibold text-white hover:bg-brand/90 disabled:opacity-60"
              >
                {update.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                Save changes
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Endpoint({ icon, label }: { icon: IconKey; label: string }) {
  const Icon = ICONS[icon];
  return (
    <div className="relative flex items-center gap-3.5 text-[13px] text-app-text-secondary">
      <span className={cn("relative z-[1] grid h-10 w-10 shrink-0 place-items-center rounded-full border-[1.5px] border-dashed border-border text-app-text", NODE_BG)}>
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
      </span>
      {label}
    </div>
  );
}

/** One saved text setting (Meetings: to-do starter categories). */
function TextSettingCard({
  card,
  ready,
  toggleReady,
  values,
  saved,
  onPatch,
}: {
  card: TextCard;
  ready: boolean;
  /** The server has the toggle's row (F1a To-do grouping). */
  toggleReady: boolean;
  values: Record<string, string>;
  saved: Record<string, string>;
  onPatch: (p: Record<string, string>) => void;
}) {
  const value = values[card.key] ?? "";
  const changed = ready && value !== (saved[card.key] ?? "");
  const problem = ready ? card.problem(value) : null;
  return (
    <section aria-label={card.title} className={cn(SECTION, "mt-4 p-6 max-sm:p-4")}>
      <h3 className="text-lg font-bold">{card.title}</h3>
      <p className="mt-0.5 text-[12.5px] text-app-text-secondary">{ready ? card.intro : `${card.intro} ${card.missing}`}</p>
      {card.toggle ? (
        <div className="mt-4">
          <SwitchRow sw={card.toggle} missing={card.toggle.missing} ready={toggleReady} values={values} saved={saved} onPatch={onPatch} />
        </div>
      ) : null}
      <textarea
        aria-label={card.title}
        disabled={!ready}
        rows={3}
        value={value}
        placeholder={card.placeholder}
        onChange={(e) => onPatch({ [card.key]: e.target.value })}
        className={cn(
          "mt-4 w-full resize-y rounded-[10px] border border-border bg-background px-3 py-2 text-sm text-app-text placeholder:text-app-text-secondary/70 disabled:opacity-60",
          changed && "border-amber-500",
          problem && "border-red-500",
        )}
      />
      {problem ? (
        <span className="mt-1 block text-xs text-red-700 dark:text-red-300">{problem}</span>
      ) : changed ? (
        <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
          Was <s>{saved[card.key] ? saved[card.key] : "the built-in 14"}</s>
        </span>
      ) : null}
    </section>
  );
}

/** One saved on/off switch (Cost savers, Listen, My notes × meeting report, To-do grouping). */
function SwitchRow({
  sw,
  missing,
  ready,
  values,
  saved,
  onPatch,
}: {
  sw: Switch;
  missing: string;
  ready: boolean;
  values: Record<string, string>;
  saved: Record<string, string>;
  onPatch: (p: Record<string, string>) => void;
}) {
  const on = values[sw.key] === "true";
  const changed = ready && (values[sw.key] ?? "") !== (saved[sw.key] ?? "");
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3", CARD, changed && CARD_CHANGED, !ready && "opacity-60")}>
      <div className="min-w-0 flex-[1_1_280px]">
        <b className="block text-sm font-semibold">{sw.name}</b>
        <small className="block text-xs text-app-text-secondary">{ready ? sw.line : `${sw.line} ${missing}`}</small>
        {changed ? (
          <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
            Was <s>{saved[sw.key] === "true" ? "On" : "Off"}</s>
          </span>
        ) : null}
      </div>
      {ready ? (
        <Seg
          label=""
          ariaLabel={sw.name}
          items={[{ value: "false", label: "Off" }, { value: "true", label: "On" }]}
          value={on ? "true" : "false"}
          onPick={(v) => onPatch({ [sw.key]: v })}
        />
      ) : null}
    </div>
  );
}

function Inspector({
  step,
  mod,
  ctx,
  values,
  saved,
  serverHas,
  langLabel,
  onPatch,
}: {
  step: StepView;
  mod: ModuleDef;
  ctx: Ctx;
  values: Record<string, string>;
  saved: Record<string, string>;
  serverHas: (key: string) => boolean;
  langLabel: string;
  onPatch: (p: Record<string, string | undefined>) => void;
}) {
  const Icon = ICONS[step.icon];
  const showLang = !mod.noLang && (step.perLang || step.id === "i_speak");
  const chain = step.perLang ? chainFor(values, ctx.lang) : null;
  const P = ctx.provider.toUpperCase();
  const llmModels = ctx.catalog.LLM[ctx.provider]?.models ?? [];
  const sub = (title: string, children: ReactNode) => (
    <div>
      <h4 className={cn(KICKER, "mb-2")}>{title}</h4>
      {children}
    </div>
  );
  const radio = (choices: StepView["choices"], picked: string, onPick: (v: string) => void, aria: string) => (
    <div role="radiogroup" aria-label={aria} className="flex flex-col gap-2">
      {choices.map((c) => {
        const on = c.value === picked;
        return (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={!!c.disabled}
            onClick={() => onPick(c.value)}
            className={cn(
              "grid w-full grid-cols-[18px_minmax(0,1fr)_auto] items-center gap-2.5 px-4 py-3 text-left text-app-text",
              CARD,
              on && CARD_ON,
              c.disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <span className={cn("grid h-4 w-4 place-items-center rounded-full border-[1.5px] border-border", on && "border-brand")}>
              {on ? <span className="h-2 w-2 rounded-full bg-brand" /> : null}
            </span>
            <span className="flex min-w-0 items-center gap-2.5">
              <VendorBadge id={c.badge} />
              <span className="min-w-0">
                <b className={cn("block text-sm font-semibold", on && "text-brand")}>{c.name}</b>
                {c.disabled || c.note ? <small className="block text-xs text-app-text-secondary">{c.disabled ?? c.note}</small> : null}
              </span>
            </span>
            <span className="whitespace-nowrap text-[13px] font-semibold tabular-nums text-app-text-secondary">
              {c.cost === undefined ? "" : fmtP(c.cost)}
              {c.cost ? <small className="text-[10.5px]">{mod.key === "ask" ? "/100q" : "/h"}</small> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
  const plain = (text: string) => <div className="rounded-xl bg-black/[0.03] px-3 py-2.5 text-[13px] text-app-text-secondary dark:bg-white/[0.04]">{text}</div>;

  let backups: ReactNode;
  if (chain) {
    backups = chain.on && chain.order.length > 1 ? (
      <ol className="flex flex-col gap-1.5">
        {chain.order.slice(1).map((v, j, rest) => (
          <li key={v} className="flex items-center gap-2.5 rounded-xl border border-dashed border-border px-2.5 py-2 text-[13px]">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-black/[0.04] font-mono text-[11px] text-app-text-secondary dark:bg-white/[0.06]">{j + 1}</span>
            <VendorBadge id={v} />
            {step.choices.find((c) => c.value === v)?.name ?? v}
            <span className="ml-auto flex gap-1">
              <button type="button" aria-label="Move up" disabled={j === 0} onClick={() => onPatch(chainPatch(ctx.lang, moveBackup(chain.order, j, -1)))} className="rounded-full p-1 text-app-text-secondary hover:bg-black/[0.06] disabled:opacity-30 dark:hover:bg-white/[0.08]">
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button type="button" aria-label="Move down" disabled={j === rest.length - 1} onClick={() => onPatch(chainPatch(ctx.lang, moveBackup(chain.order, j, 1)))} className="rounded-full p-1 text-app-text-secondary hover:bg-black/[0.06] disabled:opacity-30 dark:hover:bg-white/[0.08]">
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </span>
          </li>
        ))}
      </ol>
    ) : plain(chain.on ? "No second vendor in this order." : "Backup chain is off: Deepgram for every recording, no second vendor.");
  } else if (step.id === "i_translate" && values.MT_PROVIDER === "soniox") {
    backups = (
      <div className="flex flex-col gap-2">
        <p className="text-[13px] text-app-text-secondary">If Soniox cannot translate a turn, this OpenAI model does:</p>
        {radio(
          step.choices.filter((c) => c.value !== "soniox").map((c) => ({ ...c, disabled: isRetired(c.value.replace(/^model:/, "")) ? RETIRED_NOTE : undefined })),
          `model:${values.OPENAI_MODEL_MT ?? ""}`,
          (v) => onPatch({ OPENAI_MODEL_MT: v.replace(/^model:/, "") }),
          "Backup model",
        )}
      </div>
    );
  } else if (step.backups?.length) {
    backups = (
      <>
        <ol className="flex flex-col gap-1.5">
          {step.backups.map((b, j) => (
            <li key={b} className="flex items-center gap-2.5 rounded-xl border border-dashed border-border px-2.5 py-2 text-[13px]">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-black/[0.04] font-mono text-[11px] text-app-text-secondary dark:bg-white/[0.06]">{j + 1}</span>
              {b}
            </li>
          ))}
        </ol>
        <p className="mt-1.5 text-xs text-app-text-secondary">Order set by the server; vendors without a key are skipped.</p>
      </>
    );
  } else {
    backups = plain(`${step.backText ?? "No backup"}. Nothing to set here.`);
  }

  return (
    <aside aria-label="Step settings" className={cn(SECTION, "overflow-hidden lg:sticky lg:top-4")}>
      <div className="flex items-center gap-3.5 px-6 pb-3.5 pt-5 max-sm:px-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand text-white">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
        </span>
        <div className="min-w-0">
          <h3 className="text-lg font-bold">
            {step.name}
            {showLang ? ` · ${langLabel}` : ""}
          </h3>
          <p className="mt-0.5 text-[12.5px] text-app-text-secondary">{step.why}</p>
        </div>
      </div>
      <div className="flex flex-col gap-[18px] px-6 pb-6 pt-1 max-sm:px-4">
        {step.shared ? plain(`${step.shared}: a change here changes Meetings too.`) : null}
        {chain ? (
          <Seg
            label="Backup chain"
            items={[{ value: "true", label: "On" }, { value: "false", label: "Off, Deepgram only" }]}
            value={values.ASR_CHAIN_ENABLED === "true" ? "true" : "false"}
            onPick={(v) => onPatch({ ASR_CHAIN_ENABLED: v })}
          />
        ) : null}
        {sub(
          step.id === "m_write" ? `Model for meetings under ${values.MEETING_REPORT_LONG_MIN ?? "?"} min` : `Main vendor${step.perLang ? ` for ${langLabel}` : ""}`,
          step.choices.length > 1 ? radio(step.choices, step.picked, (v) => onPatch(pickPatch(step, v, values, ctx)), "Main vendor") : radio(step.choices, step.picked, () => {}, "Main vendor"),
        )}
        {step.id === "m_write" ? (
          <>
            {sub(
              "Model for long meetings",
              radio(
                [
                  ...(isRetired(values[`${P}_MODEL_MEETING_REPORT_LONG`])
                    ? [{ value: values[`${P}_MODEL_MEETING_REPORT_LONG`], badge: ctx.provider, name: values[`${P}_MODEL_MEETING_REPORT_LONG`], disabled: RETIRED_NOTE }]
                    : []),
                  ...llmModels.map((m) => ({ value: m.value, badge: ctx.provider, name: m.label.split(" (")[0], note: /\(([^)]*)\)/.exec(m.label)?.[1] })),
                ],
                values[`${P}_MODEL_MEETING_REPORT_LONG`] ?? "",
                (v) => onPatch({ [`${P}_MODEL_MEETING_REPORT_LONG`]: v }),
                "Long-meeting model",
              ),
            )}
            <Seg
              label="Long from"
              items={["45", "60", "90", "120", "180"].map((m) => ({ value: m, label: `${m} min` }))}
              value={values.MEETING_REPORT_LONG_MIN ?? null}
              onPick={(m) => onPatch({ MEETING_REPORT_LONG_MIN: m })}
            />
          </>
        ) : null}
        {sub("If it fails", backups)}
        {step.switches?.length
          ? sub(
              "Soniox fixes",
              <div className="flex flex-col gap-2">
                {step.switches.map((sw) => (
                  <SwitchRow key={sw.key} sw={sw} missing="Arrives with the B16 backend deploy." ready={serverHas(sw.key)} values={values} saved={saved} onPatch={onPatch} />
                ))}
                {step.shared ? <p className="text-xs text-app-text-secondary">Same switches as Meetings.</p> : null}
              </div>,
            )
          : null}
        {step.id === "i_speak" ? sub(`Voices for ${langLabel}`, <Voices values={values} ctx={ctx} onPatch={onPatch} />) : null}
        <div className="flex items-center gap-2 border-t border-border/60 pt-3.5 text-[12.5px] text-app-text-secondary">
          {step.keyOk === true ? (
            <CircleCheck className="h-4 w-4 shrink-0 text-emerald-500" />
          ) : step.keyOk === false ? (
            <CircleAlert className="h-4 w-4 shrink-0 text-amber-500" />
          ) : (
            <Info className="h-4 w-4 shrink-0" />
          )}
          <span>{step.keyText}</span>
        </div>
      </div>
    </aside>
  );
}

function Voices({ values, ctx, onPatch }: { values: Record<string, string>; ctx: Ctx; onPatch: (p: Record<string, string>) => void }) {
  const tts = values.TTS_PROVIDER || "auto";
  const name = ctx.catalog.TTS[tts]?.label.split(" (")[0] ?? tts;
  const note = (t: string) => <div className="rounded-xl bg-black/[0.03] px-3 py-2.5 text-[13px] text-app-text-secondary dark:bg-white/[0.04]">{t}</div>;
  if (tts === "auto") return note("Auto picks the voice for each language.");
  if (ctx.lang !== "urdu") return note(`${name} picks its voice for each language. Only the Urdu voices are set here.`);
  const pickers = urduVoiceSteps(tts, ctx.catalog);
  if (!pickers.length) return note(`${name} has no Urdu voice; Urdu turns use the next voice.`);
  return (
    <div className="flex flex-col gap-2">
      {[...pickers].reverse().map((p) => (
        <div key={p.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-2.5 max-sm:grid-cols-1">
          <span className="text-[13px]">{p.key.endsWith("FEMALE") ? "Female speakers hear" : "Male speakers hear"}</span>
          <Select value={values[p.key] ?? ""} onValueChange={(v) => onPatch({ [p.key]: v })}>
            <SelectTrigger aria-label={p.label} className="h-10 rounded-full">
              <SelectValue placeholder="Not set" />
            </SelectTrigger>
            <SelectContent>
              {p.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}
